import { AbstractControl, ValidationErrors } from '@angular/forms';
import * as moment from 'moment';
import * as momenttz from 'moment-timezone';
import { DateRegExp } from '../shared/app.constants';
import { OptOutMonths } from './models/app';
export class DateUtils {
  static getFirstDateOfMonth(): number {
    return Number(moment().startOf('month').format('MMDDYYYY'));
  }
  static getLastDateOfMonth(): number {
    return Number(moment().endOf('month').format('MMDDYYYY'));
  }
  static getFormattedDate(date: Date, format: string) {
    return moment(date).format(format);
  }

  // for previous day (24 hours)
  static getPreviousDay(date = new Date()) {
    const previous = new Date(date.getTime());
    previous.setDate(date.getDate() - 1);

    return previous;
  }

  // For dynamic days
  static getPreviousDate(day: number) {
    let date = new Date();
    const previousDay = new Date(date.getTime());
    previousDay.setDate(date.getDate() - day);
    return previousDay;
  }

  // for dynamic months
  static getPreviousMonth(month: number) {
    let date = new Date();
    const previousMonth = new Date(date.getTime());
    previousMonth.setMonth(date.getMonth() - month);
    return previousMonth;
  }

  // for dynamic years
  static getPreviousYear(year: number) {
    let date = new Date();
    const previousYear = new Date(date.getTime());
    previousYear.setFullYear(date.getFullYear() - year);
    return previousYear;
  }
  static getOptoutMonths(benefitMonth: string): OptOutMonths[] {
    let date = new Date(benefitMonth);
    let months: Array<any> = [];
    for (let index = 0; index < 12; index++) {
      const futureMonth = new Date(date.setMonth(date.getMonth() + index));
      months.push(this.getOptoutMonthFromDate(futureMonth));
      date = new Date(benefitMonth);
    }
    return months;
  }
  static getOptoutMonthFromDate(date: any): OptOutMonths {
    const month: any = {};
    month.monthName = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date) + ' ' + date.getFullYear();
    month.monthNumber = date.getMonth() + 1;
    month.month = new Date(date);
    return month;
  }
  static ToUnixDate(date: Date): number {
    const myutcdate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const unixDate = moment.utc(myutcdate).unix();

    return unixDate;
  }
  static ToUnix(date: Date): number {
    const myutcdate = Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    );
    const unixDate = moment.utc(myutcdate).unix();

    return unixDate;
  }
  static FromUnix(dateunix: number): Date {
    const dateLocal = new Date(dateunix * 1000);
    const newDateString = momenttz.tz(dateLocal, 'UTC').format('YYYY/MM/DD HH:mm:ss').toString();
    const newDate = new Date(newDateString);

    return newDate;
  }
  static getYearsList() {
    const yearRange = [];
    const year = new Date().getFullYear();
    yearRange.push(year);
    for (let i = 1; i < 10; i++) {
      yearRange.push(year + i);
    }
    return yearRange;
  }
  static getMonthsList() {
    const monthList = [];
    for (let i = 1; i <= 12; i++) {
      monthList.push(i);
    }
    return monthList;
  }
  static getExpirationMonthsList(year: number) {
    const monthList = [];
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    if (currentYear != year) {
      for (let i = 1; i <= 12; i++) {
        monthList.push(i);
      }
    } else {
      for (let i = currentMonth + 1; i <= 12; i++) {
        monthList.push(i);
      }
    }

    return monthList;
  }
  static getMMDD(date: Date) {
    let MMDD: string;
    let DD = ('0' + date.getDate()).slice(-2);
    let MM = ('0' + (date.getMonth() + 1)).slice(-2);
    MMDD = MM + DD;
    return MMDD;
  }
  static initiateDatePicker(d: Date) {
    return {
      date: {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
      },
      jsdate: d,
    };
  }
  checkDate(ctrl: AbstractControl): ValidationErrors | null {
    const pptDob = ctrl.value;
    const datePattern1 = DateRegExp.DatePattern1;
    const datePattern2 = DateRegExp.DatePattern2;
    if (pptDob && datePattern1.test(pptDob) && datePattern2.test(pptDob)) {
      if (new Date(pptDob) >= new Date()) {
        return {
          futureDate: true,
        };
      } else {
        return null;
      }
    }
    return null;
  }

  static validateDOB(dob: string): boolean {
    // 1. Check for null or empty
    if (!dob || dob.trim() === '') {
      return false;
    }

    // 2. Check format MM/DD/YYYY using regex
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dob.match(regex);
    if (!match) {
      return false;
    }

    // 3. Extract month, day, year
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // 4. Validate calendar date
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return false; // invalid date like 02/30/2025
    }

    // 5. Check if DOB is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0); // normalize
    if (date > today) {
      return false;
    }
    return true;
  }

  // UTC Date Time Stamp conversion
  static toUTCTimestamp(dateStr: string): number {
    if (!dateStr) return NaN;

    // If full ISO timestamp → use directly
    if (dateStr.includes('T')) {
      return new Date(dateStr).getTime();
    }

    // If only date → treat as start of day UTC
    const [year, month, day] = dateStr.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  }

  static toUTCStartOfDay(dateStr: string): number {
    const [year, month, day] = dateStr.split('-').map(Number);
    return Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  }

  static toUTCEndOfDay(dateStr: string): number {
    const [year, month, day] = dateStr.split('-').map(Number);
    return Date.UTC(year, month - 1, day, 23, 59, 59, 999);
  }
}
